// src/pages/admin.tsx
// Updated: 2026-09-10
// Purpose: Present administration pages beside a responsive left navigation pane.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import AdminNavigation from "@/components/admin/AdminNavigation";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { supabase } from "@/lib/supabaseClient";
import { readAdminTab, writeAdminTab } from "@/lib/adminReviewPreference";

import GeneralTab, {
  type GeneralStats,
} from "@/pages/admin/GeneralTab";
import ErrorLogsTab from "@/pages/admin/ErrorLogsTab";
import PaymentsTab from "@/pages/admin/PaymentsTab";
import SettingsTab from "@/pages/admin/SettingsTab";
import UserManagementTab from "@/pages/admin/UserManagementTab";
import GeminiVoicesTab from "@/pages/admin/GeminiVoicesTab";
import SoundEffectsTab from "@/pages/admin/SoundEffectsTab";
import MusicTab from "@/pages/admin/MusicTab";
import QueueTab from "@/pages/admin/QueueTab";
import ServicePickerTab from "@/pages/admin/ServicePickerTab";

const BUILD_STAMP = `Site last updated on ${__ADMIN_UPDATED_AT__}`;

const INITIAL_GENERAL_STATS: GeneralStats = {
  serviceStatus: "unknown",
  usageMinutes: {
    hour: 0,
    day: 0,
    week: 0,
    month: 0,
    quarter: 0,
  },
};

type AdminMetricsResponse = {
  ok: boolean;
  error?: string;
  metrics?: {
    registered_users: number;
    queued_jobs: number;
    processing_jobs: number;
    completed_jobs_90d: number;
    failed_jobs_90d: number;
    average_generation_seconds: number | null;
    last_generation_at: string | null;
    usage_minutes: GeneralStats["usageMinutes"];
    usage_estimated_minutes: GeneralStats["usageMinutes"];
    usage_unknown_durations: number;
    usage_historical_request_times: number;
  };
};

type TabKey =
  | "Overview"
  | "Voices"
  | "Sound effects"
  | "Music"
  | "Queue"
  | "Services"
  | "Error logs"
  | "Payments"
  | "Users"
  | "Settings";

const TABS: TabKey[] = [
  "Overview",
  "Queue",
  "Services",
  "Voices",
  "Sound effects",
  "Music",
  "Error logs",
  "Payments",
  "Users",
  "Settings",
];

class TabErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
  },
  {
    hasError: boolean;
    message?: string;
  }
> {
  constructor(props: {
    children: React.ReactNode;
  }) {
    super(props);

    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError(error: unknown) {
    return {
      hasError: true,
      message:
        error instanceof Error
          ? error.message
          : String(error),
    };
  }

  componentDidCatch(
    error: unknown,
    info: React.ErrorInfo,
  ) {
    console.error(
      "[AdminPage] Tab render error:",
      error,
      info,
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm">
          <div className="font-medium text-red-700">
            This tab failed to load.
          </div>

          <div className="mt-1 text-red-700/80">
            {this.state.message ||
              "An unexpected error occurred."}
          </div>

          <div className="mt-2 text-gray-600">
            Try another tab while we fix this.
            Check the browser console for details.
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function AdminPage() {
  const { user, signOut } = useAuth();
  const [tab, setTab] =
    useState<TabKey>(() => readAdminTab(user?.id, TABS) ?? "Overview");
  useEffect(() => { writeAdminTab(user?.id, tab); }, [user?.id, tab]);

  const [general, setGeneral] =
    useState<GeneralStats>(() => ({
      ...INITIAL_GENERAL_STATS,
      usageMinutes: {
        ...INITIAL_GENERAL_STATS.usageMinutes,
      },
    }));
  const [isGeneralRefreshing, setIsGeneralRefreshing] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const generalRefreshInFlight = useRef(false);

  const navigate = useNavigate();

  const refreshGeneral = useCallback(async () => {
    if (generalRefreshInFlight.current) return;
    generalRefreshInFlight.current = true;
    console.debug(
      "[AdminPage] Refreshing Overview metrics.",
    );

    setIsGeneralRefreshing(true);
    setGeneralError(null);

    try {
      const [metricsResult] = await Promise.allSettled([
        supabase.functions.invoke<AdminMetricsResponse>("admin-metrics", {
          body: {},
          headers: { "X-Request-ID": crypto.randomUUID() },
        }),
      ]);

      let metrics: AdminMetricsResponse["metrics"] | undefined;
      let metricsError: string | null = null;

      if (metricsResult.status === "fulfilled") {
        const { data, error } = metricsResult.value;
        if (error) {
          metricsError = error.message;
        } else if (!data?.ok || !data.metrics) {
          metricsError = data?.error || "Metrics response was incomplete.";
        } else {
          metrics = data.metrics;
        }
      } else {
        metricsError = metricsResult.reason instanceof Error
          ? metricsResult.reason.message
          : "Metrics request failed.";
      }

      if (metricsError) {
        setGeneralError(metricsError);
      }

      setGeneral((current) => ({
        ...current,
        registeredUsers: metrics?.registered_users ?? current.registeredUsers,
        queuedJobs: metrics?.queued_jobs ?? current.queuedJobs,
        processingJobs: metrics?.processing_jobs ?? current.processingJobs,
        completedJobs90d: metrics?.completed_jobs_90d ?? current.completedJobs90d,
        failedJobs90d: metrics?.failed_jobs_90d ?? current.failedJobs90d,
        averageGenerationSeconds:
          metrics?.average_generation_seconds ?? current.averageGenerationSeconds,
        lastGenerationAt: metrics?.last_generation_at ?? current.lastGenerationAt,
        usageMinutes: metrics?.usage_minutes ?? current.usageMinutes,
        usageEstimatedMinutes: metrics?.usage_estimated_minutes ?? current.usageEstimatedMinutes,
        usageUnknownDurations: metrics?.usage_unknown_durations ?? current.usageUnknownDurations,
        usageHistoricalRequestTimes: metrics?.usage_historical_request_times ?? current.usageHistoricalRequestTimes,
      }));

      console.debug(
        "[AdminPage] Overview metrics refreshed:",
        {
          metrics,
          metricsError,
        },
      );
    } catch (error) {
      console.error(
        "[AdminPage] Overview metrics request failed:",
        error,
      );

      setGeneralError(
        error instanceof Error ? error.message : "Operational metrics could not be refreshed.",
      );
    } finally {
      generalRefreshInFlight.current = false;
      setIsGeneralRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void refreshGeneral();
  }, [refreshGeneral]);

  const onSignOut = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error(
        "[AdminPage] Sign-out failed:",
        error,
      );
    }
  };

  const content = useMemo(() => {
    switch (tab) {
      case "Overview":
        return (
          <GeneralTab
            general={general}
            isRefreshing={isGeneralRefreshing}
            error={generalError}
            onOpenErrorLogs={() => setTab("Error logs")}
            onRefresh={() => {
              void refreshGeneral();
            }}
          />
        );

      case "Error logs":
        return <ErrorLogsTab />;

      case "Voices":
        return <GeminiVoicesTab key={user?.id} userId={user?.id} />;

      case "Sound effects":
        return <SoundEffectsTab />;
      case "Music":
        return <MusicTab />;

      case "Queue":
        return <QueueTab />;
      case "Services":
        return <ServicePickerTab />;

      case "Payments":
        return <PaymentsTab />;

      case "Users":
        return <UserManagementTab />;

      case "Settings":
        return <SettingsTab />;

      default:
        return null;
    }
  }, [
    general,
    generalError,
    isGeneralRefreshing,
    refreshGeneral,
    tab,
    user?.id,
  ]);

  return (
    <div className="mx-auto min-h-screen max-w-[1600px] p-4 md:p-6">
      <header className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold">
            TableRead.tv Administration
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">{BUILD_STAMP}</p>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 xl:justify-end">
          <div className="min-w-0 wrap-break-word text-sm text-muted-foreground">
            Signed in as:{" "}
            <span className="font-medium">
              {user?.email || "—"}
            </span>
          </div>

          <Button
            variant="secondary"
            onClick={onSignOut}
          >
            Sign out
          </Button>
        </div>
      </header>

      <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-start md:gap-6">
        <AdminNavigation<TabKey> tabs={TABS} activeTab={tab} onSelect={setTab} />
        <main aria-label={tab} className="min-w-0 flex-1">
          <Card>
            <CardContent className="p-4 md:p-6">
              <TabErrorBoundary key={tab}>
                {content}
              </TabErrorBoundary>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
}
